import type {
  BillingAccount,
  BillingV2CataloguePlan,
} from "@screencloud/studio-billing-sdk/client";
import { formatMoney } from "./format";
import { getBillingSubscritionRemainDays } from "./subscription";

/** The plan level the trial UI shows: Core or Pro. */
export type PlanTier = "core" | "pro";

/** Per screen, per month prices of a plan, formatted, e.g. `£18`. */
export interface TrialExpiredPricing {
  monthly: string;
  yearly: string;
}

/** The tier of a billing v2 plan: `core-studio` → `core`. Null for any other plan. */
export const toBillingV2PlanTier = (planId: string): PlanTier | null => {
  const tier = planId.replace(/-studio$/, "");
  return tier === "core" || tier === "pro" ? tier : null;
};

export interface BillingV2Trial {
  isInTrial: boolean;
  /** Days left in the trial, or null when not in trial or billing sent no end date. */
  remainingTrialDays: number | null;
  hasPaymentSource: boolean;
  tier: PlanTier | null;
}

/** The trial state of a billing v2 account, in the shape the trial banner reads. */
export const toBillingV2Trial = (account: BillingAccount): BillingV2Trial => {
  const { subscription } = account;
  const isInTrial = subscription.status === "in_trial";
  return {
    isInTrial,
    remainingTrialDays: isInTrial
      ? getBillingSubscritionRemainDays(subscription)
      : null,
    hasPaymentSource: account.paymentSources.length > 0,
    tier: toBillingV2PlanTier(subscription.plan.id),
  };
};

/**
 * The trial expired modal's prices for a tier, from the billing v2 catalogue (already in the customer's currency).
 * The annual price is shown per month.
 *
 * @returns Null when the tier has no monthly or annual price.
 */
export const toBillingV2TrialExpiredPricing = (
  catalogue: BillingV2CataloguePlan[],
  tier: PlanTier,
): TrialExpiredPricing | null => {
  const plan = catalogue.find(({ id }) => toBillingV2PlanTier(id) === tier);
  const priceFor = (periodUnit: "month" | "year") =>
    plan?.prices.find(
      (price) => price.periodUnit === periodUnit && price.period === 1,
    );
  const monthly = priceFor("month");
  const yearly = priceFor("year");
  if (!monthly || !yearly) {
    return null;
  }
  return {
    monthly: formatMoney(monthly.currencyCode, monthly.unitPrice),
    yearly: formatMoney(yearly.currencyCode, Math.round(yearly.unitPrice / 12)),
  };
};
